// The fsbo_seller_cluster SQL function answers per KEY ("these accounts
// share this browser", "these share this name"); the reviewer thinks per
// ACCOUNT ("Solange Assier: same browser and same name, pending review").
// This pivots the one into the other and decides which accounts a block
// should take along by default (#24256).

export type ClusterKind =
  | 'browser_id'
  | 'display_name'
  | 'address'
  | 'ip_network';

// Shape of fsbo_seller_cluster_result.accounts (hasura-crm migration).
export type ClusterAccount = {
  lot_id: string;
  user_id: string;
  name: string | null;
  status: string;
  blocked: boolean;
  batch_id?: string | null;
  created_at: string;
};

export type ClusterRow = {
  kind: string;
  value: string;
  other_accounts: number;
  network_owner?: string | null;
  accounts: unknown;
};

export type LinkedAccount = {
  userId: string;
  name: string | null;
  /** Their newest listing, to link to. */
  latestLotId: string;
  /** Statuses of their listings, newest first, duplicates kept. */
  statuses: string[];
  blocked: boolean;
  batchId: string | null;
  /** Which keys link this account to the listing under review. */
  kinds: ClusterKind[];
  /** For an ip_network link: who owns the range, if the submission knew. */
  networkOwner: string | null;
};

// Keys strong enough to block on without a second look: the browser is the
// seller's own device, the name and the address are what the operator
// retypes on every throwaway account. A shared IP range is every customer
// of a carrier or every user of a VPN exit, so it is shown but not ticked.
export const STRONG_KINDS: ReadonlySet<ClusterKind> = new Set([
  'browser_id',
  'display_name',
  'address',
]);

const KIND_ORDER: readonly ClusterKind[] = [
  'browser_id',
  'display_name',
  'address',
  'ip_network',
];

const isKind = (kind: string): kind is ClusterKind =>
  (KIND_ORDER as readonly string[]).includes(kind);

export const parseAccounts = (raw: unknown): ClusterAccount[] =>
  Array.isArray(raw)
    ? raw.filter(
        (a): a is ClusterAccount =>
          typeof a === 'object' &&
          a != null &&
          typeof (a as ClusterAccount).lot_id === 'string' &&
          typeof (a as ClusterAccount).user_id === 'string',
      )
    : [];

type Accumulating = LinkedAccount & { newest: string; lots: Set<string> };

/**
 * One row per other account, strongest link first, then most recent.
 * `hiddenAccounts` counts accounts the SQL function capped off (it returns
 * at most 50 listings per key) so the card can say "and N more".
 */
export const pivotLinkedAccounts = (
  rows: readonly ClusterRow[],
): { accounts: LinkedAccount[]; hiddenAccounts: number } => {
  const byUser = new Map<string, Accumulating>();
  let hidden = 0;
  for (const row of rows) {
    if (!isKind(row.kind) || row.other_accounts === 0) {
      continue;
    }
    const accounts = parseAccounts(row.accounts);
    const shownUsers = new Set(accounts.map(a => a.user_id));
    hidden = Math.max(hidden, row.other_accounts - shownUsers.size);
    for (const a of accounts) {
      const existing = byUser.get(a.user_id);
      if (existing == null) {
        byUser.set(a.user_id, {
          userId: a.user_id,
          name: a.name?.trim() || null,
          latestLotId: a.lot_id,
          statuses: [a.status],
          blocked: a.blocked,
          batchId: a.batch_id ?? null,
          kinds: [row.kind],
          networkOwner:
            row.kind === 'ip_network' ? row.network_owner ?? null : null,
          newest: a.created_at,
          lots: new Set([a.lot_id]),
        });
        continue;
      }
      if (!existing.kinds.includes(row.kind)) {
        existing.kinds.push(row.kind);
      }
      if (row.kind === 'ip_network' && existing.networkOwner == null) {
        existing.networkOwner = row.network_owner ?? null;
      }
      // The same listing shows up under every key it matches; count it
      // once. A different listing of the same account is a second status.
      if (!existing.lots.has(a.lot_id)) {
        existing.lots.add(a.lot_id);
        existing.statuses.push(a.status);
      }
      if (a.created_at > existing.newest) {
        existing.newest = a.created_at;
        existing.latestLotId = a.lot_id;
      }
      existing.blocked = existing.blocked || a.blocked;
      existing.batchId = existing.batchId ?? a.batch_id ?? null;
    }
  }

  const rank = (acc: LinkedAccount) =>
    Math.min(...acc.kinds.map(k => KIND_ORDER.indexOf(k)));
  const accounts = [...byUser.values()]
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.newest < b.newest ? 1 : a.newest > b.newest ? -1 : 0),
    )
    .map(({ newest: _newest, lots: _lots, ...acc }) => ({
      ...acc,
      kinds: [...acc.kinds].sort(
        (a, b) => KIND_ORDER.indexOf(a) - KIND_ORDER.indexOf(b),
      ),
    }));
  return { accounts, hiddenAccounts: hidden };
};

/** True when this account is linked by at least one strong key. */
export const isStrongLink = (account: LinkedAccount): boolean =>
  account.kinds.some(k => STRONG_KINDS.has(k));

/** The accounts a block takes along unless the reviewer unticks them. */
export const defaultBlockSelection = (
  accounts: readonly LinkedAccount[],
): string[] =>
  accounts.filter(a => !a.blocked && isStrongLink(a)).map(a => a.userId);

/**
 * The batch a new block should join: that of a linked account already
 * blocked (strongest link first, which is the order `accounts` comes in),
 * so one "unblock all" later undoes the whole group. Only a STRONG link
 * qualifies: an account related by nothing more than an IP range is not one
 * the block asserts a link with (it is not even pre-ticked), so it must not
 * drag the new block into its batch — or a later "unblock all" would lift
 * two unrelated advertisers together.
 */
export const batchToJoin = (
  accounts: readonly LinkedAccount[],
): string | null =>
  accounts.find(a => a.blocked && a.batchId != null && isStrongLink(a))
    ?.batchId ?? null;
